import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../ui/dialog';
import {
  Bot, Play, Loader2, CheckCircle2, XCircle, AlertTriangle,
  Trash2, Eye, RefreshCw, Bug, ChevronDown, ChevronRight,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const STATUS_COLORS = {
  success: 'bg-green-100 text-green-800',
  partial: 'bg-yellow-100 text-yellow-800',
  failed: 'bg-red-100 text-red-800',
  running: 'bg-blue-100 text-blue-800',
};

const SEVERITY_COLORS = {
  P0: 'bg-red-600 text-white',
  P1: 'bg-orange-500 text-white',
  P2: 'bg-yellow-400 text-slate-900',
};

export const BotTesterTab = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [selectedRun, setSelectedRun] = useState(null);
  const [cleaning, setCleaning] = useState(false);
  const [showSteps, setShowSteps] = useState(false);

  const loadHistory = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/bot-tester/history`);
      setRuns(res.data.runs || []);
    } catch (e) {
      toast.error('Error cargando historial');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadHistory(); }, []);

  const runTests = async () => {
    if (!window.confirm('¿Ejecutar todas las pruebas? Se crearán pedidos de test con el cliente purolink7@gmail.com.')) return;
    setRunning(true);
    try {
      const res = await axios.post(`${API}/admin/bot-tester/run`);
      const run = res.data;
      if (run.status === 'success') {
        toast.success(`✅ Todos los tests pasaron (${run.flows_passed}/${run.flows_total})`);
      } else if (run.status === 'partial') {
        toast.warning(`⚠️ ${run.flows_passed}/${run.flows_total} OK, ${run.bugs_found} bugs encontrados`);
      } else {
        toast.error(`❌ Tests fallaron: ${run.bugs_found} bugs`);
      }
      await loadHistory();
      setSelectedRun(run);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error ejecutando tests');
    } finally {
      setRunning(false);
    }
  };

  const viewDetail = async (runId) => {
    try {
      const res = await axios.get(`${API}/admin/bot-tester/history/${runId}`);
      setSelectedRun(res.data);
      setShowSteps(false);
    } catch (e) {
      toast.error('Error cargando detalle');
    }
  };

  const deleteRun = async (runId) => {
    if (!window.confirm('¿Eliminar este registro de ejecución?')) return;
    try {
      await axios.delete(`${API}/admin/bot-tester/history/${runId}`);
      toast.success('Ejecución eliminada');
      loadHistory();
    } catch (e) {
      toast.error('Error eliminando');
    }
  };

  const cleanupTestData = async () => {
    if (!window.confirm('¿Eliminar TODOS los pedidos de test generados por el bot? Esta acción no se puede deshacer.')) return;
    setCleaning(true);
    try {
      const res = await axios.post(`${API}/admin/bot-tester/cleanup`);
      toast.success(`${res.data.deleted} pedidos de test eliminados`);
    } catch (e) {
      toast.error('Error limpiando');
    } finally {
      setCleaning(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  };

  const runDuration = (run) => {
    if (!run.finished_at || !run.started_at) return '-';
    const ms = new Date(run.finished_at) - new Date(run.started_at);
    return ms < 1000 ? `${ms}ms` : `${(ms / 1000).toFixed(1)}s`;
  };

  // Latest open bugs (from last completed run)
  const latestRun = runs[0];
  const openBugs = latestRun?.bugs || [];

  return (
    <div className="space-y-6" data-testid="bot-tester-tab">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold flex items-center gap-2">
            <Bot className="h-6 w-6 text-indigo-600" />
            Bot Tester
          </h2>
          <p className="text-muted-foreground text-sm">
            Agente automático que simula pedidos, recargas y cambios de estado para detectar bugs.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={loadHistory}
            variant="outline"
            size="sm"
            disabled={loading}
            data-testid="bot-refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Actualizar
          </Button>
          <Button
            onClick={cleanupTestData}
            variant="outline"
            size="sm"
            disabled={cleaning}
            data-testid="bot-cleanup-btn"
          >
            {cleaning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Trash2 className="h-4 w-4 mr-2" />}
            Limpiar datos test
          </Button>
          <Button
            onClick={runTests}
            disabled={running}
            className="bg-indigo-600 hover:bg-indigo-700"
            data-testid="bot-run-btn"
          >
            {running ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Play className="h-4 w-4 mr-2" />}
            {running ? 'Ejecutando...' : 'Ejecutar pruebas'}
          </Button>
        </div>
      </div>

      {/* Config summary */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm">Configuración del bot</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-1">
          <div>🧑 Cliente test: <code className="bg-muted px-1 rounded">purolink7@gmail.com</code></div>
          <div>✍️ Editor test: <code className="bg-muted px-1 rounded">rfmiranda73@gmail.com</code></div>
          <div>🌐 Productos: <code className="bg-muted px-1 rounded">web_email=info@jrfa3.com</code></div>
          <div className="pt-2 text-xs text-muted-foreground">
            Los pedidos generados llevan <code>test_flag=bot_tester</code> y pueden limpiarse con el botón "Limpiar datos test".
          </div>
        </CardContent>
      </Card>

      {/* Stats */}
      {latestRun && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs text-muted-foreground">Última ejecución</div>
              <div className="text-sm font-semibold mt-1">{formatDate(latestRun.started_at)}</div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs text-muted-foreground">Resultado</div>
              <Badge className={`${STATUS_COLORS[latestRun.status] || ''} mt-1`}>
                {latestRun.status}
              </Badge>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs text-muted-foreground">Flujos OK</div>
              <div className="text-2xl font-bold text-green-600 mt-1">
                {latestRun.flows_passed}/{latestRun.flows_total}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs text-muted-foreground">Emails enviados</div>
              <div className="text-2xl font-bold text-blue-600 mt-1">
                {latestRun.emails_sent?.length || 0}
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-xs text-muted-foreground">Bugs encontrados</div>
              <div className="text-2xl font-bold text-red-600 mt-1 flex items-center gap-2">
                <Bug className="h-5 w-5" />
                {latestRun.bugs_found || 0}
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Open bugs (last run) */}
      {openBugs.length > 0 && (
        <Card className="border-red-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2 text-red-700">
              <AlertTriangle className="h-5 w-5" />
              Bugs abiertos (última ejecución)
            </CardTitle>
            <CardDescription>Encontrados el {formatDate(latestRun.started_at)}</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {openBugs.map((bug, i) => (
              <div key={i} className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
                <Badge className={SEVERITY_COLORS[bug.severity] || ''}>{bug.severity}</Badge>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-sm">{bug.title}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    Flujo: <span className="font-medium">{bug.flow}</span>
                  </div>
                  {(bug.expected || bug.found) && (
                    <div className="text-xs mt-1 font-mono">
                      <span className="text-green-700">Esperaba:</span> {bug.expected || '—'} <br />
                      <span className="text-red-700">Encontrado:</span> {bug.found || '—'}
                    </div>
                  )}
                  {bug.context && <div className="text-xs text-slate-500 mt-1">{bug.context}</div>}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* History */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Historial de ejecuciones</CardTitle>
          <CardDescription>Últimas 50 ejecuciones del bot</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : runs.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground text-sm">
              No hay ejecuciones aún. Pulsa "Ejecutar pruebas" para empezar.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fecha</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Duración</TableHead>
                  <TableHead>Flujos OK</TableHead>
                  <TableHead>Bugs</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {runs.map((run) => (
                  <TableRow key={run.id} data-testid={`bot-run-row-${run.id}`}>
                    <TableCell className="text-sm">{formatDate(run.started_at)}</TableCell>
                    <TableCell>
                      <Badge className={STATUS_COLORS[run.status] || ''}>
                        {run.status === 'success' && <CheckCircle2 className="h-3 w-3 mr-1 inline" />}
                        {run.status === 'failed' && <XCircle className="h-3 w-3 mr-1 inline" />}
                        {run.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-sm">{runDuration(run)}</TableCell>
                    <TableCell className="text-sm">
                      {run.flows_passed || 0}/{run.flows_total || 0}
                    </TableCell>
                    <TableCell>
                      {run.bugs_found > 0 ? (
                        <Badge variant="destructive">{run.bugs_found}</Badge>
                      ) : (
                        <span className="text-green-600 text-sm">0</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => viewDetail(run.id)}>
                        <Eye className="h-4 w-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteRun(run.id)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Detail Dialog */}
      <Dialog open={!!selectedRun} onOpenChange={(o) => !o && setSelectedRun(null)}>
        <DialogContent className="max-w-4xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Bot className="h-5 w-5" />
              Ejecución del {selectedRun && formatDate(selectedRun.started_at)}
            </DialogTitle>
            <DialogDescription>
              {selectedRun && (
                <>
                  Estado: <Badge className={STATUS_COLORS[selectedRun.status] || ''}>{selectedRun.status}</Badge>
                  {' · '}Duración: {selectedRun && runDuration(selectedRun)}
                  {' · '}Flujos: {selectedRun?.flows_passed || 0}/{selectedRun?.flows_total || 0} OK
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          {selectedRun && (
            <div className="space-y-4">
              {/* Emails sent */}
              {selectedRun.emails_sent?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    📧 Emails disparados ({selectedRun.emails_sent.length})
                  </h3>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead className="bg-slate-100">
                        <tr>
                          <th className="px-2 py-1 text-left">Flujo</th>
                          <th className="px-2 py-1 text-left">Para</th>
                          <th className="px-2 py-1 text-left">Asunto</th>
                          <th className="px-2 py-1 text-left">Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedRun.emails_sent.map((em, i) => (
                          <tr key={i} className="border-t">
                            <td className="px-2 py-1 text-muted-foreground">{em.flow}</td>
                            <td className="px-2 py-1 font-mono">{em.to}</td>
                            <td className="px-2 py-1 truncate max-w-[280px]" title={em.subject}>{em.subject}</td>
                            <td className="px-2 py-1">
                              <Badge variant={em.status === 'sent' ? 'default' : 'destructive'} className="text-[10px]">
                                {em.status || '?'}
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {/* Bugs */}
              {selectedRun.bugs?.length > 0 && (
                <div>
                  <h3 className="font-semibold text-sm mb-2 flex items-center gap-2">
                    <Bug className="h-4 w-4 text-red-600" />
                    Bugs ({selectedRun.bugs.length})
                  </h3>
                  <div className="space-y-2">
                    {selectedRun.bugs.map((bug, i) => (
                      <div key={i} className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm">
                        <div className="flex items-center gap-2">
                          <Badge className={SEVERITY_COLORS[bug.severity] || ''}>{bug.severity}</Badge>
                          <span className="font-medium">{bug.title}</span>
                        </div>
                        <div className="text-xs text-muted-foreground mt-1">Flujo: {bug.flow}</div>
                        {(bug.expected || bug.found) && (
                          <pre className="text-xs bg-white p-2 mt-2 rounded border font-mono whitespace-pre-wrap">
                            Esperaba: {bug.expected || '—'}{'\n'}Encontrado: {bug.found || '—'}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Steps collapsible */}
              <div>
                <button
                  type="button"
                  onClick={() => setShowSteps(!showSteps)}
                  className="flex items-center gap-2 text-sm font-semibold hover:text-indigo-600"
                >
                  {showSteps ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                  Pasos detallados ({selectedRun.steps?.length || 0})
                </button>
                {showSteps && (
                  <div className="mt-2 space-y-1 max-h-96 overflow-y-auto border rounded-lg p-2 bg-slate-50">
                    {(selectedRun.steps || []).map((step, i) => (
                      <div key={i} className={`text-xs p-2 rounded ${step.ok ? 'bg-green-50 border-l-2 border-green-400' : 'bg-red-50 border-l-2 border-red-400'}`}>
                        <div className="flex items-center gap-2">
                          {step.ok ? <CheckCircle2 className="h-3 w-3 text-green-600" /> : <XCircle className="h-3 w-3 text-red-600" />}
                          <span className="font-medium">{step.flow}</span>
                          <span className="text-muted-foreground">→</span>
                          <span>{step.step}</span>
                        </div>
                        {step.detail && <div className="ml-5 text-muted-foreground mt-0.5">{step.detail}</div>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BotTesterTab;
